/**
 * Switch System Tool - BIM 2025 Grade
 * 
 * Creates logical relationships between switches and lighting fixtures.
 * Supports:
 * - Single Pole, 3-Way, 4-Way switching systems
 * - Automatic wire generation path (logical)
 * - System browser integration data
 * - multi-switch control groups
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';

export interface SwitchSystemGeometryData {
    masterSwitchId: string;
    secondarySwitchIds: string[]; // For 3-way/4-way
    controlledFixtureIds: string[];
}

export interface SwitchSystemElement extends Element {
    category: 'Switch Systems';
    geometryData: SwitchSystemGeometryData;

    // System Data
    systemData: {
        name: string;
        type: 'Single Pole' | '3-Way' | '4-Way' | 'Dimmer';
    };
}

export class SwitchSystemTool extends BaseTool {
    id = 'switch_system';
    name = 'Switch System';
    cursor = 'crosshair';

    private masterSwitchId: string | null = null;
    private secondarySwitchIds: string[] = [];
    private controlledFixtureIds: string[] = [];

    private mode: 'select_master' | 'select_secondary' | 'select_fixtures' = 'select_master';

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = event.intersections.find(i => {
            const id = this.findId(i.object);
            return id && id !== 'ground-plane';
        });

        if (!hit) return;

        const id = this.findId(hit.object)!;
        const element = this.context.bimStore.project.elements.get(id);

        if (!element) return;

        // selection logic
        if (this.mode === 'select_master') {
            if (this.isValidSwitch(element)) {
                this.masterSwitchId = id;
                // Auto-advance based on modifiers? For now manual advancement or context menu would be ideal.
                // We'll increment to fixtures by default, user can press 'S' for secondary switches
                this.mode = 'select_fixtures';

                // Feedback
                console.log('Master switch selected:', id);
            }
        } else if (this.mode === 'select_secondary') {
            if (this.isValidSwitch(element) && element.id !== this.masterSwitchId) {
                if (!this.secondarySwitchIds.includes(id)) {
                    this.secondarySwitchIds.push(id);
                    console.log('Secondary switch added:', id);
                }
            }
        } else if (this.mode === 'select_fixtures') {
            if (element.category === 'Lighting Fixtures') {
                if (!this.controlledFixtureIds.includes(id)) {
                    this.controlledFixtureIds.push(id);
                    console.log('Fixture added:', id);
                } else {
                    // Toggle off
                    this.controlledFixtureIds = this.controlledFixtureIds.filter(fid => fid !== id);
                    console.log('Fixture removed:', id);
                }
            }
        }
    }

    private isValidSwitch(element: Element): boolean {
        // Check if it's a lighting device or electrical fixture with "Switch" in type/category
        return (element.category === 'Electrical Fixtures' || element.category === 'Lighting Devices') &&
            (element.type.includes('Switch') || element.parameters?.['Load Classification'] === 'Switch');
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const hit = event.intersections.find(i => {
            const id = this.findId(i.object);
            return id && id !== 'ground-plane';
        });

        if (hit) {
            const id = this.findId(hit.object)!;
            const element = this.context.bimStore.project.elements.get(id);

            let color = undefined;

            if (this.mode === 'select_master') {
                if (element && this.isValidSwitch(element)) color = '#00FF00'; // Green
            } else if (this.mode === 'select_secondary') {
                if (element && this.isValidSwitch(element)) color = '#00FFFF'; // Cyan
            } else if (this.mode === 'select_fixtures') {
                if (element?.category === 'Lighting Fixtures') {
                    color = this.controlledFixtureIds.includes(id) ? '#FFFF00' : '#808000'; // Bright yellow if selected
                }
            }

            if (color) {
                this.context.appStore.setToolPreview({
                    type: 'switch_system',
                    hoveredId: id,
                    color
                });
            } else {
                this.context.appStore.setToolPreview(null);
            }
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (!this.context) return;

        if (event.key === 'Enter') {
            if (this.masterSwitchId && this.controlledFixtureIds.length > 0) {
                this.createSwitchSystem();
            }
        } else if (event.key === 'Escape') {
            this.reset();
        } else if (event.key.toLowerCase() === 's') {
            // Mode switch to secondary
            if (this.mode === 'select_fixtures' || this.mode === 'select_master') {
                this.mode = 'select_secondary';
                console.log('Mode: Select Secondary Switches');
            }
        } else if (event.key.toLowerCase() === 'f') {
            this.mode = 'select_fixtures';
            console.log('Mode: Select Fixtures');
        }
    }

    private reset() {
        this.masterSwitchId = null;
        this.secondarySwitchIds = [];
        this.controlledFixtureIds = [];
        this.mode = 'select_master';
        this.context?.appStore.setToolPreview(null);
    }

    private createSwitchSystem() {
        if (!this.context || !this.masterSwitchId) return;

        const geometryData: SwitchSystemGeometryData = {
            masterSwitchId: this.masterSwitchId,
            secondarySwitchIds: this.secondarySwitchIds,
            controlledFixtureIds: this.controlledFixtureIds
        };

        const isMultiWay = this.secondarySwitchIds.length > 0;
        const systemType = isMultiWay
            ? (this.secondarySwitchIds.length === 1 ? '3-Way' : '4-Way')
            : 'Single Pole';

        const switchSystem: SwitchSystemElement = {
            id: uuidv4(),
            category: 'Switch Systems',
            type: `Switch System: ${systemType}`,
            parameters: {
                'System Name': `SW-${uuidv4().substring(0, 4)}`,
                'Type': systemType,
                'Number of Fixtures': this.controlledFixtureIds.length.toString(),
                'Master Switch': this.masterSwitchId
            },
            systemData: {
                name: `SW-${uuidv4().substring(0, 4)}`,
                type: systemType
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(switchSystem);

        // Update connected elements (back-references) if we had a robust update system
        // for (const fid of this.controlledFixtureIds) ...

        this.reset();
    }
}
